import React, { useState, useMemo, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import './DateRangePickerModal.scss';

const DateRangePickerModal = ({ title, onClose, onGenerate, minStartDate }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const getToday = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const normalizeDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };

  const parseMinStartDate = (input) => {
    if (!input) return null;
    
    let date;
    if (input instanceof Date) {
      date = input;
    } else {
      try {
        date = new Date(String(input).trim());
      } catch (error) {
        return null;
      }
    }
    
    if (isNaN(date.getTime())) return null;
    
    const today = getToday();
    if (date > today) date = today;
    
    return normalizeDate(date);
  };

  const minStartDateObj = useMemo(() => parseMinStartDate(minStartDate), [minStartDate]);

  useEffect(() => {
    if (!minStartDateObj) return;
    
    const min = normalizeDate(minStartDateObj);
    if (!min) return;
    
    if (startDate) {
      const start = normalizeDate(startDate);
      if (start && start < min) {
        setStartDate(new Date(minStartDateObj));
      }
    }
    
    if (endDate) {
      const end = normalizeDate(endDate);
      const validStart = normalizeDate(startDate || minStartDateObj);
      if (end && validStart && end < validStart) {
        setEndDate(new Date(startDate || minStartDateObj));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minStartDateObj]);

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return;
    }
    
    let start = normalizeDate(startDate);
    let end = normalizeDate(endDate);
    
    if (!start || !end) return;
    
    const min = minStartDateObj ? normalizeDate(minStartDateObj) : null;
    const today = normalizeDate(getToday());
    
    let corrected = false;
    
    if (min && start < min) {
      start = new Date(minStartDateObj);
      start.setHours(0, 0, 0, 0);
      corrected = true;
    }
    
    if (end < start) {
      end = new Date(start);
      corrected = true;
    }
    
    if (today && end > today) {
      end = new Date(today);
      corrected = true;
    }
    
    if (corrected) {
      setStartDate(start);
      setEndDate(end);
      return;
    }
    
    const formattedStart = formatDate(start);
    const formattedEnd = formatDate(end);
    
    if (!formattedStart || !formattedEnd) return;
    
    try {
      if (onGenerate) {
        const result = onGenerate({ startDate: formattedStart, endDate: formattedEnd });
        if (result && typeof result.then === 'function') {
          await result;
        }
      }
      if (onClose) onClose();
    } catch (error) {
      console.error('DateRangePickerModal: Error in callbacks', error);
    }
  };

  return (
    <div className="date-range-picker-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className="date-range-picker-modal">
        <div className="modal-header">
          <h3>{title || 'Select Date Range'}</h3>
        </div>
        
        <div className="modal-body">
          <div className="date-field">
            <label>Start Date</label>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              minDate={minStartDateObj}
              maxDate={endDate || getToday()}
              placeholderText="Select start date"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
            />
          </div>
          
          <div className="date-field">
            <label>End Date</label>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate || minStartDateObj}
              maxDate={getToday()}
              placeholderText="Select end date"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
            />
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="cancel-button" onClick={() => onClose && onClose()}>
            Cancel
          </button>
          <button 
            className="generate-button" 
            onClick={handleGenerate}
            disabled={!startDate || !endDate}
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangePickerModal;



// WEBPACK FOOTER //
// ./src/components/DateRangePickerModal/DateRangePickerModal.js